import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [isCheckingSession, setIsCheckingSession] = useState(true)
  const [hasValidSession, setHasValidSession] = useState(false)
  
  const navigate = useNavigate()
  const { checkAuthentication } = useAuth()

  useEffect(() => {
    // When Supabase redirects here after email verification, we need to handle the token
    const verifyResetSession = async () => {
      try {
        // Debug: Log current URL
        console.log('Current URL:', window.location.href)
        console.log('Hash:', window.location.hash)
        console.log('Search params:', window.location.search)
        
        // Check URL for Supabase auth hash fragment
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        let accessToken = hashParams.get('access_token')
        let refreshToken = hashParams.get('refresh_token')
        let type = hashParams.get('type')
        
        // Also check query parameters (Supabase sometimes uses these instead of hash)
        if (!accessToken) {
          const queryParams = new URLSearchParams(window.location.search)
          accessToken = queryParams.get('access_token') || queryParams.get('token')
          refreshToken = queryParams.get('refresh_token')
          type = queryParams.get('type') || 'recovery'
          
          console.log('Checking query params:', {
            accessToken: accessToken ? 'present' : 'missing',
            refreshToken: refreshToken ? 'present' : 'missing',
            type: type
          })
        }
        
        console.log('Parsed params:', {
          accessToken: accessToken ? 'present' : 'missing',
          refreshToken: refreshToken ? 'present' : 'missing',
          type: type,
          source: hashParams.get('access_token') ? 'hash' : 'query'
        })
        
        if (accessToken && (type === 'recovery' || type === 'magiclink')) {
          // We have a recovery token from Supabase
          console.log('Found recovery token in URL, attempting to verify...')
          
          // Exchange the token for a session
          const response = await fetch('/api/v1/auth/verify-recovery-token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ 
              access_token: accessToken,
              refresh_token: refreshToken || accessToken // Use refresh_token if available
            })
          })
          
          console.log('Verify token response status:', response.status)
          const data = await response.json()
          console.log('Verify token response:', data)
          
          if (response.ok && data.success) {
            setHasValidSession(true)
            console.log('Recovery token verified successfully')
            // Store both tokens for subsequent requests
            if (accessToken) {
              sessionStorage.setItem('recovery_token', accessToken)
              if (refreshToken) {
                sessionStorage.setItem('recovery_refresh_token', refreshToken)
              }
            }
            // Clear the hash from URL for security
            window.history.replaceState(null, '', window.location.pathname)
          } else {
            console.error('Token verification failed:', data.error)
            setError('Invalid or expired reset link. Please request a new password reset.')
          }
        } else {
          // No token in URL, check if we have an existing session
          console.log('No recovery token in URL, checking for existing session...')
          
          const response = await fetch('/api/v1/auth/session', {
            method: 'GET',
            credentials: 'include'
          })
          
          console.log('Session check response status:', response.status)
          
          if (response.ok) {
            const data = await response.json()
            console.log('Session data:', data)
            
            if (data.user) {
              setHasValidSession(true)
              console.log('Valid reset session found')
            } else {
              console.log('No user in session')
              setError('Invalid or expired reset link. Please request a new password reset.')
            }
          } else {
            console.error('Session check failed')
            setError('Invalid or expired reset link. Please request a new password reset.')
          }
        }
      } catch (err) {
        console.error('Failed to verify session:', err)
        setError('Unable to verify reset link. Please try again.')
      } finally {
        setIsCheckingSession(false)
      }
    }
    
    verifyResetSession()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Get the recovery token from session storage
      const recoveryToken = sessionStorage.getItem('recovery_token')
      
      // Update password using the active session
      const response = await fetch('/api/v1/auth/update-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(recoveryToken && { 'Authorization': `Bearer ${recoveryToken}` })
        },
        credentials: 'include',
        body: JSON.stringify({ password })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Clean up recovery token
        sessionStorage.removeItem('recovery_token')
        
        // Show success message
        alert(data.message || 'Password updated successfully!')
        
        // Redirect to login page
        navigate(data.data?.redirect || '/login')
      } else {
        // Log the response for debugging
        console.error('Password reset failed:', data)
        setError(data.error || 'Failed to reset password')
      }
    } catch (err) {
      console.error('Password update error:', err)
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isCheckingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
          Set new password
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
              <p>{error}</p>
              <p className="text-sm mt-2">
                <a href="/forgot-password" className="underline">
                  Request a new password reset
                </a>
              </p>
            </div>
          )}

          {hasValidSession ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-card-foreground">
                  New password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter new password"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-card-foreground">
                  Confirm new password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading || !password || !confirmPassword}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Updating...' : 'Update password'}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted-foreground mb-4">
                This password reset link is invalid or has expired.
              </p>
              <a
                href="/forgot-password"
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                Request a new password reset
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage